import type { FaceitMatches, FaceitMatchFill, FindFaceitMatchInput, TeamFaceit } from "@69ly/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiFailure } from "./api.ts";

export const faceitKeys = {
  team: (id: string) => ["faceit", "team", id] as const,
  matches: (id: string) => ["faceit", "matches", id] as const,
};

/** The team and FACEIT: members' profiles, the linked FACEIT team and what it can be linked to. */
export function useTeamFaceit(teamId: string | undefined) {
  return useQuery({ queryKey: faceitKeys.team(teamId ?? ""), queryFn: () => api<TeamFaceit>("GET", `/teams/${teamId}/faceit`), enabled: !!teamId, staleTime: 5 * 60_000 });
}

/** Looks the members up on FACEIT again (a new account, a changed nickname). */
export function useRefreshTeamFaceit(teamId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<TeamFaceit>("GET", `/teams/${teamId}/faceit?refresh=1`),
    onSuccess: (data) => {
      qc.setQueryData(faceitKeys.team(teamId), data);
      void qc.invalidateQueries({ queryKey: faceitKeys.matches(teamId) });
    },
  });
}

export function useLinkFaceitTeam(teamId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (faceitTeamId: string | null) => api<TeamFaceit>("PUT", `/teams/${teamId}/faceit`, { teamId: faceitTeamId }),
    onSuccess: (data) => {
      qc.setQueryData(faceitKeys.team(teamId), data);
      void qc.invalidateQueries({ queryKey: faceitKeys.matches(teamId) });
    },
  });
}

/** The team's FACEIT matches and championships: asked of FACEIT, so only when shown. */
export function useFaceitMatches(teamId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: faceitKeys.matches(teamId ?? ""),
    queryFn: () => api<FaceitMatches>("GET", `/teams/${teamId}/faceit/matches`),
    enabled: !!teamId && enabled,
    staleTime: 2 * 60_000,
    retry: (count, error) => !(error instanceof ApiFailure && (error.code === "faceit_off" || error.status === 404)) && count < 1,
  });
}

/** The FACEIT match of a demo being imported; null when FACEIT is off, does not know it, or fails. */
export async function findFaceitMatch(input: Partial<FindFaceitMatchInput>): Promise<FaceitMatchFill | null> {
  try {
    return (await api<{ fill: FaceitMatchFill | null }>("POST", "/faceit/find", input)).fill;
  } catch {
    return null;
  }
}
